import { format } from 'date-fns'
import { getTimezoneOffsetInHours } from 'www/utils'
import type { ScheduleType } from 'www/api/apiScheduleTelemed.types'
import {
  getItemDataWithParent,
  type ScheduleModuleData,
} from 'www/modules/Schedule/functions/helpers'

import {
  getDoctorRequestInterface,
  getDoctorOnlineRequestInterface,
} from 'www/modules/Schedule/models'
import type {
  DoctorRequestInterface,
  DoctorOnlineRequestInterface,
} from 'www/modules/Schedule/models/request'
import type {
  getDoctorsStorageInterface,
  getDoctorsLpuStorageInterface,
} from 'www/modules/Schedule/models/storage'

type DoctorsStorage =
  | ReturnType<typeof getDoctorsStorageInterface>
  | ReturnType<typeof getDoctorsLpuStorageInterface>

type DoctorsSingleScheduleData = ScheduleModuleData & {
  storage: DoctorsStorage
}

/**
 * @description
 * Собирает данные для запроса за расписанием для 1 врача.
 * Если timezone пользователя и сервера совпадают - запроса быть не должно.
 *
 * @param opts.data - данные 'ScheduleData'
 * @param opts.isOnlineSelected - является ли выбранным таб 'Онлайн'
 * @param opts.target - узел, который послужил триггером события
 * @param opts.scheduleType -
 *
 * @returns объект в формате 'getDoctorRequestInterface' | 'getDoctorOnlineRequestInterface' если данные успешно собраны.
 * Если не найдено данных для врача или timezone пользователя и сервера совпадает - undefined, так как запрос в этих случаях не нужен
 */

const getDoctorsRequestDataSingle = ({
  data,
  isOnlineSelected,
  isTelemedMedtochkaSelected,
  scheduleType,
  target,
}: {
  data: DoctorsSingleScheduleData
  isOnlineSelected?: boolean
  isTelemedMedtochkaSelected?: boolean
  scheduleType: ScheduleType
  target: HTMLElement
}): DoctorRequestInterface | DoctorOnlineRequestInterface | undefined => {
  const {
    dateStart,
    timedelta,
    doctorsLpus,
  } = data.storage

  const isAnyTelemed = Boolean(isOnlineSelected || isTelemedMedtochkaSelected)
  const baseData = isAnyTelemed ? getDoctorOnlineRequestInterface() : getDoctorRequestInterface()

  const [, itemData] = getItemDataWithParent({ data, target })

  if (!itemData || !('doctorId' in itemData)) {
    return undefined
  }

  const chosenDoctorId = itemData.doctorId
  const { coverNode } = itemData

  const chosenLpuId = Number(
    coverNode.dataset.wpBlockId
      || coverNode.querySelector<HTMLOptionElement>('option:checked')?.dataset.lpu, // ToDo schedule-important избавиться от привязки к DOM дереву
  )

  const doctorHasSlots = doctorsLpus
    .find(item => {
      const hasSlots = 'has_slots' in item ? item.has_slots : undefined

      return (
        (typeof hasSlots === 'undefined' ? true : hasSlots) // на странице врачей клиники поле 'has_slots' отсутствует
        && item.doctor_id === chosenDoctorId
        && item.lpu_id === chosenLpuId
      )
    })

  if (!doctorHasSlots) {
    return undefined
  }

  if (!isAnyTelemed) {
    (baseData as DoctorRequestInterface).doctors_lpus = [doctorHasSlots]
  }

  if (isAnyTelemed) {
    const telemedData = baseData as DoctorOnlineRequestInterface

    telemedData.user_start_date = format(new Date(), 'yyyy-MM-dd')
    telemedData.user_timezone = getTimezoneOffsetInHours()
    telemedData.lpu_params = [{
      lpu_id: doctorHasSlots.lpu_id,
      schedule_params: [{
        doctor_id: doctorHasSlots.doctor_id,
        type_schedule: scheduleType,
      }],
    }]
  } else {
    const doctorData = baseData as DoctorRequestInterface

    doctorData.dt_start = dateStart
    doctorData.town_timedelta = timedelta
  }

  return baseData
}

export default getDoctorsRequestDataSingle
